import React from 'react';
import "./smile-pages.css";

export default function HowDoesSmilingReducePain() {
  return (
    <div className="container-box mt-5 mb-5 smile-page">

      <h1>How does Smiling Reduce Pain?</h1>

      <p>
        Smiling reduces pain by triggering <strong>biological and psychological responses </strong> 
        that help your brain and body cope more effectively with discomfort. Even a forced 
        or intentional smile can activate <strong>pain-relief pathways</strong>, lower perceived pain 
        intensity, and help shift your focus away from the discomfort.
      </p>

      <p>Here’s how it works, backed by scientific evidence:</p>

      <hr />

      {/* --- 1. Natural Painkillers --- */}
      <h3>🌺 1. Smiling Releases Natural Painkillers</h3>

      <p>When you smile, your brain releases:</p>

      <ul>
        <li><strong>Endorphins</strong> – the body’s natural opioids that reduce pain and increase pleasure</li>
        <li><strong>Dopamine</strong> – enhances mood and lowers pain perception</li>
        <li><strong>Serotonin</strong> – regulates mood and boosts resilience to pain</li>
      </ul>

      <p>
        These <strong>neurochemicals</strong> work together to dull the sensation of pain and elevate 
        your emotional response to it.
      </p>

      <p>
        Smiling is <strong>similar to the “runner’s high”</strong> but triggered by facial muscle movement.
      </p>

      <h4>📚 Source:</h4>
      <ul>
        <li>Scott, D. J., et al. (2008). <em>Individual differences in reward responding explain placebo-induced expectations and effects.</em></li>
        <li>Kraft & Pressman, 2012 — Found smiling during a painful cold pressor test reduced reported pain levels.</li>
        <li>Dill & Anderson, 1995 — Smiling increased pain tolerance.</li>
      </ul>

      <hr />

      {/* --- 2. Parasympathetic System --- */}
      <h3>🧘‍♀️ 2. Smiling Activates the Parasympathetic Nervous System</h3>

      <p>
        Smiling — especially when paired with deep breathing — activates the 
        <strong> “rest and digest” system</strong> (parasympathetic nervous system), which:
      </p>

      <ul>
        <li><strong>Relaxes</strong> the body</li>
        <li>Slows the heart rate</li>
        <li>Reduces the perception of pain by <strong>lowering stress levels</strong></li>
      </ul>

      <h4>📚 Study:</h4>
      <p>
        Kraft & Pressman (2012) showed that smiling during stress can lead to 
        <strong> lower heart rates</strong> and <strong>faster recovery</strong>, which also correlates 
        with a <strong>reduction in discomfort.</strong>
      </p>

      <hr />

      {/* --- 3. Facial Feedback --- */}
      <h3>🍀 3. Facial Feedback Loop Reduces Pain Perception</h3>

      <p>
        The <strong>Facial Feedback Hypothesis</strong> suggests that your facial expressions influence 
        your emotional state — and by extension, your pain response.
      </p>

      <ul>
        <li>Smiling tricks your brain into thinking you’re feeling better.</li>
        <li>That perceived improvement leads to <strong>real reductions in pain sensitivity.</strong></li>
      </ul>

      <h4>📚 Study:</h4>
      <p>
        A 2020 study from the University of California, Irvine, found that people who 
        <strong> smiled or grimaced</strong> during a painful needle injection reported 
        <strong> about 40% less pain</strong> than those who kept neutral expressions.
      </p>

      <hr />

      {/* --- 4. Shifts Focus --- */}
      <h3>🎯 4. Shifts Focus Away <em>From</em> Pain</h3>

      <p>
        Smiling — especially when combined with laughter or positive thoughts — helps 
        <strong> distract the brain</strong> from pain signals.
      </p>

      <ul>
        <li>
          The brain processes emotional and pain signals in overlapping regions, such as 
          the anterior cingulate cortex.
        </li>
        <li>
          Positive emotional input (like smiling) competes with pain signals, 
          <strong> reducing their intensity.</strong>
        </li>
      </ul>

      <h4>📚 Source:</h4>
      <p>
        Villemure & Bushnell (2002). 
        <em> Cognitive modulation of pain: how do attention and emotion influence pain processing?</em>
      </p>

      <hr />

      {/* --- Summary Table --- */}
      <h3>✅ Summary: How Smiling Reduces Pain</h3>

      <table className="summary-table">
        <thead>
          <tr>
            <th>Mechanism</th>
            <th>Effect on Pain</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Endorphin release</td>
            <td>Acts as a natural painkiller</td>
          </tr>
          <tr>
            <td>Nervous system relaxation</td>
            <td>Reduces muscle tension and stress-induced pain</td>
          </tr>
          <tr>
            <td>Facial feedback</td>
            <td>Decreases perception of discomfort</td>
          </tr>
          <tr>
            <td>Attention redirection</td>
            <td>Distracts from and downregulates pain</td>
          </tr>
          <tr>
            <td>Improved emotional state</td>
            <td>Increases tolerance and pain resilience</td>
          </tr>
        </tbody>
      </table>

      <hr />

      {/* --- Keys to Remember --- */}
      <h3>🔑 Keys to Remember</h3>

      <p>
        <strong>Smiling doesn’t just make you feel better — it can actually help you hurt less. </strong> 
        Whether you're dealing with physical pain or emotional discomfort, a simple smile can 
        activate your body's natural healing chemistry and reduce how intensely you experience pain.
      </p>
      <p>
        <strong> That Smile App is here to help ease pain.</strong>
      </p>

    </div>
  );
}
